import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ErrorState } from '@/components/feedback/ErrorState';
import { BrandLoader } from '@/components/feedback/BrandLoader';
import { isApiError } from '@/lib/api/client';
import { env } from '@/lib/env';
import { useAuth } from '@/lib/auth/auth-provider';
import { useSession } from '@/app/session';

/**
 * Route guards.
 *
 * They are a UX affordance, never a security control: every endpoint is also
 * protected server side. Their job is to avoid rendering a screen the user will
 * not be allowed to use.
 */

interface GuardProps {
  children?: ReactNode;
}

/** Requires an authenticated + locally authorized user. */
export function ProtectedRoute({ children }: GuardProps): JSX.Element {
  const { status: authStatus, error: authError, signIn } = useAuth();
  const { user, isLoading, error } = useSession();

  if (authStatus === 'error') {
    return (
      <div className="p-4 md:p-6">
        <ErrorState
          variant="connection"
          title="No pudimos iniciar sesión"
          description={
            authError?.message ??
            'No fue posible obtener tu identidad de Microsoft. Vuelve a intentarlo.'
          }
          onRetry={() => void signIn()}
          retryLabel="Iniciar sesión"
        />
      </div>
    );
  }

  if (authStatus === 'unauthenticated' && !env.isDevAuth) {
    return (
      <div className="p-4 md:p-6">
        <ErrorState
          variant="forbidden"
          title="Inicia sesión para continuar"
          description="Usa tu cuenta corporativa de Microsoft para ver la disponibilidad de las salas."
          onRetry={() => void signIn()}
          retryLabel="Iniciar sesión"
        />
      </div>
    );
  }

  if (isLoading) return <BrandLoader label="Comprobando tu acceso…" />;

  if (isApiError(error) && error.isForbidden) {
    return <Navigate to="/403" replace />;
  }

  if (error) {
    return (
      <div className="p-4 md:p-6">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (!user) return <Navigate to="/unavailable" replace />;

  return <>{children ?? <Outlet />}</>;
}

/** Requires the `admin` role; falls back to /403 keeping the attempted URL. */
export function AdminRoute({ children }: GuardProps): JSX.Element {
  const { isAdmin, isLoading, user } = useSession();
  const location = useLocation();

  if (isLoading) return <BrandLoader label="Comprobando tu acceso…" />;

  if (!user || !isAdmin) {
    return <Navigate to="/403" replace state={{ from: location.pathname }} />;
  }

  return <>{children ?? <Outlet />}</>;
}
